import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconFilter,
  IconSearch,
  IconDiscount2,
  IconGift,
  IconScale,
  IconTrendingDown,
  IconReceipt2,
  IconArrowDownRight,
  IconArrowUpRight,
  IconCalendar,
  IconToolsKitchen2,
  IconCoins,
  IconFileSpreadsheet,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useDiscountsAndDeductionsReport } from "../controllers/reports.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import ReportExportWrapper from "../components/ReportExportWrapper";

export default function DiscountRoundOffReportPage() {
  const { t } = useTranslation();

  const filterTypeRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();

  const now = new Date();
  const defaultDateFrom = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const defaultDateTo = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

  const [state, setState] = useState({
    filter: "today",
    fromDate: null,
    toDate: null,
    search: "",
    activeTab: "overview", // 'overview' | 'discounts' | 'complementary' | 'round_off'
  });

  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "NPR";

  const { data, isLoading, error } = useDiscountsAndDeductionsReport({
    dateFilter: state.filter,
    from: state.fromDate,
    to: state.toDate,
  });

  const filters = [
    { key: "today", value: "Today" },
    { key: "yesterday", value: "Yesterday" },
    { key: "last_7days", value: "Last 7 Days" },
    { key: "this_month", value: "This Month" },
    { key: "last_month", value: "Last Month" },
    { key: "custom", value: "Custom Date" },
  ];

  const summary = data?.summary || {
    totalDiscountAmount: 0,
    discountInvoicesCount: 0,
    avgDiscountPerBill: 0,
    totalComplementaryAmount: 0,
    totalComplementaryItemsCount: 0,
    complementaryOrdersCount: 0,
    netRoundOffAmount: 0,
    roundOffInvoicesCount: 0,
    roundOffNegativeAmount: 0,
    roundOffNegativeCount: 0,
    roundOffPositiveAmount: 0,
    roundOffPositiveCount: 0,
    totalConcessionsValue: 0,
  };

  const rawDiscounts = data?.discounts || [];
  const rawComplementary = data?.complementary || [];
  const rawRoundOffs = data?.roundOffs || [];

  // Filtered lists based on search
  const filteredDiscounts = useMemo(() => {
    if (!state.search) return rawDiscounts;
    const q = state.search.toLowerCase();
    return rawDiscounts.filter(
      (item) =>
        item.invoice_no?.toLowerCase().includes(q) ||
        item.token_no?.toString().includes(q) ||
        item.customer_name?.toLowerCase().includes(q) ||
        item.customer_phone?.toLowerCase().includes(q) ||
        item.table_title?.toLowerCase().includes(q) ||
        item.payment_type?.toLowerCase().includes(q) ||
        item.billed_by?.toLowerCase().includes(q)
    );
  }, [rawDiscounts, state.search]);

  const filteredComplementary = useMemo(() => {
    if (!state.search) return rawComplementary;
    const q = state.search.toLowerCase();
    return rawComplementary.filter(
      (item) =>
        item.item_name?.toLowerCase().includes(q) ||
        item.order_id?.toString().includes(q) ||
        item.token_no?.toString().includes(q) ||
        item.invoice_no?.toLowerCase().includes(q) ||
        item.table_title?.toLowerCase().includes(q) ||
        item.staff_name?.toLowerCase().includes(q) ||
        item.notes?.toLowerCase().includes(q)
    );
  }, [rawComplementary, state.search]);

  const filteredRoundOffs = useMemo(() => {
    if (!state.search) return rawRoundOffs;
    const q = state.search.toLowerCase();
    return rawRoundOffs.filter(
      (item) =>
        item.invoice_no?.toLowerCase().includes(q) ||
        item.token_no?.toString().includes(q) ||
        item.customer_name?.toLowerCase().includes(q) ||
        item.table_title?.toLowerCase().includes(q) ||
        item.payment_type?.toLowerCase().includes(q)
    );
  }, [rawRoundOffs, state.search]);

  // Export columns and data based on active tab
  const { exportColumns, exportData } = useMemo(() => {
    if (state.activeTab === "discounts") {
      return {
        exportColumns: [
          { header: "Invoice #", key: "invoice_no" },
          { header: "Date & Time", key: "created_at" },
          { header: "Order/Token", key: "order_info" },
          { header: "Customer", key: "customer" },
          { header: "Table/Delivery", key: "service_info" },
          { header: "Subtotal", key: "sub_total" },
          { header: "Discount Amt", key: "discount" },
          { header: "Discount %", key: "discount_percent" },
          { header: "Round Off", key: "round_off" },
          { header: "Total Paid", key: "paid_amount" },
          { header: "Payment Mode", key: "payment_type" },
        ],
        exportData: filteredDiscounts.map((d) => ({
          invoice_no: d.invoice_no || `#${d.invoice_id}`,
          created_at: new Date(d.created_at).toLocaleString(),
          order_info: d.token_no ? `Token #${d.token_no}` : (d.order_id ? `Order #${d.order_id}` : "-"),
          customer: d.customer_name || d.customer_phone || "Walk-in",
          service_info: d.table_title || d.delivery_type || "Dine In",
          sub_total: `${currency} ${Number(d.sub_total || 0).toFixed(2)}`,
          discount: `${currency} ${Number(d.discount || 0).toFixed(2)}`,
          discount_percent: `${d.discount_percent || 0}%`,
          round_off: `${currency} ${Number(d.round_off || 0).toFixed(2)}`,
          paid_amount: `${currency} ${Number(d.paid_amount || d.total || 0).toFixed(2)}`,
          payment_type: d.payment_type || "Cash",
        })),
      };
    }

    if (state.activeTab === "complementary") {
      return {
        exportColumns: [
          { header: "Item Name", key: "item_name" },
          { header: "Date & Time", key: "item_date" },
          { header: "Order/Token", key: "order_info" },
          { header: "Table/Area", key: "table_title" },
          { header: "Quantity", key: "quantity" },
          { header: "Normal Price", key: "price" },
          { header: "Waived Value", key: "total_value" },
          { header: "Staff", key: "staff_name" },
          { header: "Notes", key: "notes" },
        ],
        exportData: filteredComplementary.map((c) => ({
          item_name: c.item_name || "Complementary Item",
          item_date: new Date(c.item_date || c.order_date).toLocaleString(),
          order_info: c.token_no ? `Token #${c.token_no}` : `Order #${c.order_id}`,
          table_title: c.table_title || c.delivery_type || "Table",
          quantity: c.quantity,
          price: `${currency} ${Number(c.price || 0).toFixed(2)}`,
          total_value: `${currency} ${Number(c.total_value || 0).toFixed(2)}`,
          staff_name: c.staff_name || "Staff",
          notes: c.notes || "-",
        })),
      };
    }

    if (state.activeTab === "round_off") {
      return {
        exportColumns: [
          { header: "Invoice #", key: "invoice_no" },
          { header: "Date & Time", key: "created_at" },
          { header: "Order/Token", key: "order_info" },
          { header: "Table", key: "table_title" },
          { header: "Bill Before Rounding", key: "pre_round_total" },
          { header: "Round Off (+/-)", key: "round_off" },
          { header: "Direction", key: "direction" },
          { header: "Final Payable", key: "total" },
          { header: "Payment Mode", key: "payment_type" },
        ],
        exportData: filteredRoundOffs.map((r) => ({
          invoice_no: r.invoice_no || `#${r.invoice_id}`,
          created_at: new Date(r.created_at).toLocaleString(),
          order_info: r.token_no ? `Token #${r.token_no}` : (r.order_id ? `Order #${r.order_id}` : "-"),
          table_title: r.table_title || "Counter",
          pre_round_total: `${currency} ${Number(r.pre_round_total || 0).toFixed(2)}`,
          round_off: `${r.round_off > 0 ? "+" : ""}${Number(r.round_off || 0).toFixed(2)}`,
          direction: r.round_off < 0 ? "Rounded Down (Discount)" : "Rounded Up",
          total: `${currency} ${Number(r.total || 0).toFixed(2)}`,
          payment_type: r.payment_type || "Cash",
        })),
      };
    }

    // Default Overview Export (combines all 3 deductions into an audit summary)
    return {
      exportColumns: [
        { header: "Category", key: "category" },
        { header: "Transaction / Item", key: "name" },
        { header: "Reference / Bill", key: "ref" },
        { header: "Date & Time", key: "date" },
        { header: "Amount / Waived Value", key: "amount" },
        { header: "Impact / Note", key: "impact" },
      ],
      exportData: [
        ...filteredDiscounts.map((d) => ({
          category: "Discount",
          name: `Bill Discount (${d.discount_percent || 0}%)`,
          ref: d.invoice_no || `Inv #${d.invoice_id}`,
          date: new Date(d.created_at).toLocaleString(),
          amount: `${currency} ${Number(d.discount || 0).toFixed(2)}`,
          impact: `Waived off on subtotal of ${currency}${Number(d.sub_total || 0).toFixed(2)}`,
        })),
        ...filteredComplementary.map((c) => ({
          category: "Complementary",
          name: `${c.quantity}x ${c.item_name}`,
          ref: c.token_no ? `Token #${c.token_no}` : `Order #${c.order_id}`,
          date: new Date(c.item_date || c.order_date).toLocaleString(),
          amount: `${currency} ${Number(c.total_value || 0).toFixed(2)}`,
          impact: `100% Complementary dish by ${c.staff_name || "Staff"}`,
        })),
        ...filteredRoundOffs.map((r) => ({
          category: "Round Off",
          name: r.round_off < 0 ? "Round Down Adjustment" : "Round Up Adjustment",
          ref: r.invoice_no || `Inv #${r.invoice_id}`,
          date: new Date(r.created_at).toLocaleString(),
          amount: `${r.round_off > 0 ? "+" : ""}${currency} ${Number(r.round_off || 0).toFixed(2)}`,
          impact: r.round_off < 0 ? "Waived fraction to nearest unit" : "Rounded up fraction",
        })),
      ],
    };
  }, [state.activeTab, filteredDiscounts, filteredComplementary, filteredRoundOffs, currency]);

  const summaryExportData = {
    "Total Discounts": `${currency} ${Number(summary.totalDiscountAmount || 0).toFixed(2)} (${summary.discountInvoicesCount} Bills)`,
    "Total Complementary": `${currency} ${Number(summary.totalComplementaryAmount || 0).toFixed(2)} (${summary.totalComplementaryItemsCount} Items in ${summary.complementaryOrdersCount} Orders)`,
    "Net Round Off Adjustment": `${summary.netRoundOffAmount >= 0 ? "+" : ""}${currency} ${Number(summary.netRoundOffAmount || 0).toFixed(2)}`,
    "Round Off Reductions (Waived)": `${currency} ${Number(summary.roundOffNegativeAmount || 0).toFixed(2)} (${summary.roundOffNegativeCount} Bills)`,
    "Round Off Additions": `+${currency} ${Number(summary.roundOffPositiveAmount || 0).toFixed(2)} (${summary.roundOffPositiveCount} Bills)`,
    "Total Revenue Concessions": `${currency} ${Number(summary.totalConcessionsValue || 0).toFixed(2)}`,
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-full min-h-[60vh]">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="text-center py-20">
          <p className="text-red-500 font-bold">Failed to load report data. Please try again.</p>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <ReportExportWrapper
        title="Discounts, Complementary & Round Off Report"
        reportType={`Deductions & Concessions (${state.activeTab.toUpperCase()})`}
        dateRange={filters.find((f) => f.key === state.filter)?.value}
        filters={state.filter === "custom" ? `${state.fromDate} to ${state.toDate}` : "None"}
        columns={exportColumns}
        tableData={exportData}
        summaryData={summaryExportData}
      >
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6 print:hidden">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-gradient-to-tr from-amber-500 to-orange-500 text-white rounded-2xl shadow-sm">
                <IconDiscount2 size={24} />
              </span>
              <div>
                <h3 className="text-2xl lg:text-3xl font-black">
                  Discounts, Complementary & Round Off
                </h3>
                <p className="text-gray-500 text-xs md:text-sm">
                  Complete audit of promotions, complimentary items, and bill rounding adjustments
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => document.getElementById("filter-dialog").showModal()}
              className="btn btn-ghost bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl gap-2 font-bold shadow-sm"
            >
              <IconFilter stroke={iconStroke} size={18} />
              <span>{filters.find((f) => f.key === state.filter)?.value}</span>
            </button>
          </div>
        </div>

        {/* 4 Summary KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Card 1: Discounts */}
          <div
            onClick={() => setState((prev) => ({ ...prev, activeTab: "discounts" }))}
            className={`p-6 rounded-3xl border transition-all cursor-pointer ${
              state.activeTab === "discounts"
                ? "bg-amber-500/10 border-amber-500 ring-2 ring-amber-500/20 shadow-md"
                : "bg-white dark:bg-restro-gray border-restro-border-green hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Discounts Given
              </span>
              <div className="w-10 h-10 rounded-2xl bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <IconDiscount2 size={22} />
              </div>
            </div>
            <h2 className="text-2xl lg:text-3xl font-black text-amber-600 dark:text-amber-400">
              {currency} {Number(summary.totalDiscountAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
              <span>{summary.discountInvoicesCount} discounted bills</span>
              <span>Avg: {currency}{Number(summary.avgDiscountPerBill || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Card 2: Complementary */}
          <div
            onClick={() => setState((prev) => ({ ...prev, activeTab: "complementary" }))}
            className={`p-6 rounded-3xl border transition-all cursor-pointer ${
              state.activeTab === "complementary"
                ? "bg-purple-500/10 border-purple-500 ring-2 ring-purple-500/20 shadow-md"
                : "bg-white dark:bg-restro-gray border-restro-border-green hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
                Complementary Items
              </span>
              <div className="w-10 h-10 rounded-2xl bg-purple-100 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                <IconGift size={22} />
              </div>
            </div>
            <h2 className="text-2xl lg:text-3xl font-black text-purple-600 dark:text-purple-400">
              {currency} {Number(summary.totalComplementaryAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
              <span>{summary.totalComplementaryItemsCount} items waived</span>
              <span>Across {summary.complementaryOrdersCount} orders</span>
            </div>
          </div>

          {/* Card 3: Round Off */}
          <div
            onClick={() => setState((prev) => ({ ...prev, activeTab: "round_off" }))}
            className={`p-6 rounded-3xl border transition-all cursor-pointer ${
              state.activeTab === "round_off"
                ? "bg-blue-500/10 border-blue-500 ring-2 ring-blue-500/20 shadow-md"
                : "bg-white dark:bg-restro-gray border-restro-border-green hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Round Off Adjustments
              </span>
              <div className="w-10 h-10 rounded-2xl bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <IconScale size={22} />
              </div>
            </div>
            <h2 className="text-2xl lg:text-3xl font-black text-blue-600 dark:text-blue-400">
              {summary.netRoundOffAmount >= 0 ? "+" : ""}
              {currency} {Number(summary.netRoundOffAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
              <span className="text-rose-500">Waived: -{currency}{Number(summary.roundOffNegativeAmount || 0).toFixed(2)}</span>
              <span className="text-emerald-500">Added: +{currency}{Number(summary.roundOffPositiveAmount || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Card 4: Total Concessions Value */}
          <div
            onClick={() => setState((prev) => ({ ...prev, activeTab: "overview" }))}
            className={`p-6 rounded-3xl border transition-all cursor-pointer ${
              state.activeTab === "overview"
                ? "bg-emerald-500/10 border-emerald-500 ring-2 ring-emerald-500/20 shadow-md"
                : "bg-white dark:bg-restro-gray border-restro-border-green hover:shadow-sm"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Total Value Concessions
              </span>
              <div className="w-10 h-10 rounded-2xl bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <IconTrendingDown size={22} />
              </div>
            </div>
            <h2 className="text-2xl lg:text-3xl font-black text-emerald-600 dark:text-emerald-400">
              {currency} {Number(summary.totalConcessionsValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium truncate">
              Combined discounts, free items & round down
            </p>
          </div>
        </div>

        {/* Tab Switcher & Search Bar */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-6 print:hidden">
          <div className="flex items-center gap-1.5 p-1.5 bg-gray-100 dark:bg-gray-800/60 rounded-2xl overflow-x-auto">
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: "overview" }))}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs md:text-sm whitespace-nowrap transition-all ${
                state.activeTab === "overview"
                  ? "bg-white dark:bg-restro-gray text-restro-green shadow-sm"
                  : "text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              Overview & All Concessions
            </button>
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: "discounts" }))}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs md:text-sm whitespace-nowrap transition-all flex items-center gap-1.5 ${
                state.activeTab === "discounts"
                  ? "bg-white dark:bg-restro-gray text-amber-600 dark:text-amber-400 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <span>Discounts</span>
              <span className="badge badge-sm bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-none font-bold">
                {rawDiscounts.length}
              </span>
            </button>
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: "complementary" }))}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs md:text-sm whitespace-nowrap transition-all flex items-center gap-1.5 ${
                state.activeTab === "complementary"
                  ? "bg-white dark:bg-restro-gray text-purple-600 dark:text-purple-400 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <span>Complementary</span>
              <span className="badge badge-sm bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-none font-bold">
                {rawComplementary.length}
              </span>
            </button>
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: "round_off" }))}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs md:text-sm whitespace-nowrap transition-all flex items-center gap-1.5 ${
                state.activeTab === "round_off"
                  ? "bg-white dark:bg-restro-gray text-blue-600 dark:text-blue-400 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <span>Round Off</span>
              <span className="badge badge-sm bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-none font-bold">
                {rawRoundOffs.length}
              </span>
            </button>
          </div>

          <div className="relative flex-1 max-w-md">
            <IconSearch
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search invoice, token, customer, item, waiter..."
              value={state.search}
              onChange={(e) => setState((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl text-xs md:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-restro-green/20"
            />
          </div>
        </div>

        {/* Tab Content 1: Overview & Audit Breakdown */}
        {state.activeTab === "overview" && (
          <div className="space-y-6">
            {/* Visual Concessions Ratio Breakdown */}
            <div className="p-6 bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] shadow-sm">
              <h4 className="text-base font-black mb-4 flex items-center gap-2">
                <IconCoins size={20} className="text-restro-green" /> Deductions & Concessions Composition
              </h4>

              {summary.totalConcessionsValue === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  No discounts, complementary items, or round-off reductions recorded for this period.
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Progress Bar */}
                  <div className="h-5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex shadow-inner">
                    {summary.totalDiscountAmount > 0 && (
                      <div
                        style={{
                          width: `${(
                            (summary.totalDiscountAmount / summary.totalConcessionsValue) *
                            100
                          ).toFixed(1)}%`,
                        }}
                        className="bg-amber-500 h-full transition-all duration-500"
                        title={`Discounts: ${currency} ${summary.totalDiscountAmount}`}
                      />
                    )}
                    {summary.totalComplementaryAmount > 0 && (
                      <div
                        style={{
                          width: `${(
                            (summary.totalComplementaryAmount / summary.totalConcessionsValue) *
                            100
                          ).toFixed(1)}%`,
                        }}
                        className="bg-purple-500 h-full transition-all duration-500"
                        title={`Complementary: ${currency} ${summary.totalComplementaryAmount}`}
                      />
                    )}
                    {summary.roundOffNegativeAmount > 0 && (
                      <div
                        style={{
                          width: `${(
                            (summary.roundOffNegativeAmount / summary.totalConcessionsValue) *
                            100
                          ).toFixed(1)}%`,
                        }}
                        className="bg-blue-500 h-full transition-all duration-500"
                        title={`Round-down: ${currency} ${summary.roundOffNegativeAmount}`}
                      />
                    )}
                  </div>

                  {/* Legend */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
                      <div className="w-3.5 h-3.5 rounded-full bg-amber-500 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 font-bold uppercase">Discounts</p>
                        <p className="text-sm font-black">
                          {currency} {Number(summary.totalDiscountAmount).toFixed(2)}{" "}
                          <span className="text-xs font-semibold text-gray-400">
                            ({summary.totalConcessionsValue > 0 ? ((summary.totalDiscountAmount / summary.totalConcessionsValue) * 100).toFixed(1) : 0}%)
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
                      <div className="w-3.5 h-3.5 rounded-full bg-purple-500 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 font-bold uppercase">Complementary</p>
                        <p className="text-sm font-black">
                          {currency} {Number(summary.totalComplementaryAmount).toFixed(2)}{" "}
                          <span className="text-xs font-semibold text-gray-400">
                            ({summary.totalConcessionsValue > 0 ? ((summary.totalComplementaryAmount / summary.totalConcessionsValue) * 100).toFixed(1) : 0}%)
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-2xl">
                      <div className="w-3.5 h-3.5 rounded-full bg-blue-500 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 font-bold uppercase">Round-Down Waived</p>
                        <p className="text-sm font-black">
                          {currency} {Number(summary.roundOffNegativeAmount).toFixed(2)}{" "}
                          <span className="text-xs font-semibold text-gray-400">
                            ({summary.totalConcessionsValue > 0 ? ((summary.roundOffNegativeAmount / summary.totalConcessionsValue) * 100).toFixed(1) : 0}%)
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Unified Recent Concessions Audit Table */}
            <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] overflow-hidden shadow-sm">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-black">Combined Deductions Audit Register</h4>
                  <p className="text-xs text-gray-400">
                    Chronological register of all promotional discounts, free items, and rounding
                  </p>
                </div>
                <span className="badge badge-ghost font-bold text-xs">
                  {exportData.length} records
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="table w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/40 text-gray-500 text-xs uppercase font-black">
                      <th>Type</th>
                      <th>Transaction / Item</th>
                      <th>Reference / Bill</th>
                      <th>Date & Time</th>
                      <th className="text-right">Amount / Value</th>
                      <th>Impact Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportData.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-gray-400">
                          No deduction transactions found for this period.
                        </td>
                      </tr>
                    ) : (
                      exportData.slice(0, 100).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition text-xs font-medium">
                          <td>
                            <span
                              className={`badge badge-sm font-black uppercase text-[10px] ${
                                row.category === "Discount"
                                  ? "badge-warning"
                                  : row.category === "Complementary"
                                  ? "badge-secondary"
                                  : "badge-info"
                              }`}
                            >
                              {row.category}
                            </span>
                          </td>
                          <td className="font-bold text-gray-800 dark:text-gray-200">
                            {row.name}
                          </td>
                          <td className="font-semibold text-gray-500">
                            {row.ref}
                          </td>
                          <td className="text-gray-400">
                            {row.date}
                          </td>
                          <td className="text-right font-black text-sm">
                            <span
                              className={
                                row.category === "Discount"
                                  ? "text-amber-600 dark:text-amber-400"
                                  : row.category === "Complementary"
                                  ? "text-purple-600 dark:text-purple-400"
                                  : "text-blue-600 dark:text-blue-400"
                              }
                            >
                              {row.amount}
                            </span>
                          </td>
                          <td className="text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {row.impact}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content 2: Discounts (Bill-Wise) */}
        {state.activeTab === "discounts" && (
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-black flex items-center gap-2">
                  <IconDiscount2 size={20} className="text-amber-500" /> Bill Discounts Register
                </h4>
                <p className="text-xs text-gray-400">
                  Individual bills where discount concessions were applied
                </p>
              </div>
              <span className="badge badge-warning font-bold text-xs">
                {filteredDiscounts.length} discounted bills
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/40 text-gray-500 text-xs uppercase font-black">
                    <th>Invoice No</th>
                    <th>Date & Time</th>
                    <th>Order / Token</th>
                    <th>Customer</th>
                    <th>Table / Delivery</th>
                    <th className="text-right">Subtotal</th>
                    <th className="text-right">Discount</th>
                    <th className="text-right">Discount %</th>
                    <th className="text-right">Round Off</th>
                    <th className="text-right">Grand Total</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiscounts.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-12 text-gray-400">
                        No discounted invoices found.
                      </td>
                    </tr>
                  ) : (
                    filteredDiscounts.map((d, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition text-xs font-medium">
                        <td className="font-black text-gray-900 dark:text-gray-100">
                          {d.invoice_no || `#${d.invoice_id}`}
                        </td>
                        <td className="text-gray-400">
                          {new Date(d.created_at).toLocaleString()}
                        </td>
                        <td>
                          <span className="font-bold text-gray-700 dark:text-gray-300">
                            {d.token_no ? `Token #${d.token_no}` : (d.order_id ? `Order #${d.order_id}` : "-")}
                          </span>
                        </td>
                        <td>
                          <p className="font-bold text-gray-800 dark:text-gray-200">
                            {d.customer_name || "Walk-in"}
                          </p>
                          {d.customer_phone && (
                            <p className="text-[10px] text-gray-400">{d.customer_phone}</p>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-sm badge-ghost font-bold">
                            {d.table_title || d.delivery_type || "Dine In"}
                          </span>
                        </td>
                        <td className="text-right font-bold text-gray-600 dark:text-gray-400">
                          {currency} {Number(d.sub_total || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-black text-amber-600 dark:text-amber-400 text-sm">
                          -{currency} {Number(d.discount || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-bold text-gray-500">
                          {d.discount_percent}%
                        </td>
                        <td className="text-right font-medium text-gray-500">
                          {Number(d.round_off) !== 0 ? (
                            <span className={d.round_off < 0 ? "text-rose-500 font-bold" : "text-emerald-500 font-bold"}>
                              {d.round_off > 0 ? "+" : ""}{Number(d.round_off).toFixed(2)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-right font-black text-gray-900 dark:text-white">
                          {currency} {Number(d.paid_amount || d.total || 0).toFixed(2)}
                        </td>
                        <td>
                          <span className="badge badge-sm badge-outline font-bold">
                            {d.payment_type || "Cash"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content 3: Complementary Items */}
        {state.activeTab === "complementary" && (
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-black flex items-center gap-2">
                  <IconGift size={20} className="text-purple-500" /> Complementary Items Register
                </h4>
                <p className="text-xs text-gray-400">
                  Individual menu items served free of charge to customers or staff
                </p>
              </div>
              <span className="badge badge-secondary font-bold text-xs">
                {filteredComplementary.length} complementary items
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/40 text-gray-500 text-xs uppercase font-black">
                    <th>Item Name</th>
                    <th>Date & Time</th>
                    <th>Order / Token</th>
                    <th>Table / Room</th>
                    <th className="text-center">Quantity</th>
                    <th className="text-right">Regular Price</th>
                    <th className="text-right">Waived Valuation</th>
                    <th>Served / Ordered By</th>
                    <th>Notes / Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComplementary.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-12 text-gray-400">
                        No complementary items found for this period.
                      </td>
                    </tr>
                  ) : (
                    filteredComplementary.map((c, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition text-xs font-medium">
                        <td>
                          <p className="font-black text-gray-900 dark:text-gray-100 text-sm">
                            {c.item_name}
                          </p>
                          {c.invoice_no && (
                            <p className="text-[10px] text-gray-400">Inv: {c.invoice_no}</p>
                          )}
                        </td>
                        <td className="text-gray-400">
                          {new Date(c.item_date || c.order_date).toLocaleString()}
                        </td>
                        <td>
                          <span className="font-bold text-gray-700 dark:text-gray-300">
                            {c.token_no ? `Token #${c.token_no}` : `Order #${c.order_id}`}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-sm badge-ghost font-bold">
                            {c.table_title || c.delivery_type || "Table"}
                          </span>
                        </td>
                        <td className="text-center font-black text-purple-600 dark:text-purple-400">
                          {c.quantity}
                        </td>
                        <td className="text-right font-medium text-gray-500">
                          {currency} {Number(c.price || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-black text-purple-600 dark:text-purple-400 text-sm">
                          {currency} {Number(c.total_value || 0).toFixed(2)}
                        </td>
                        <td>
                          <span className="font-bold text-gray-700 dark:text-gray-300">
                            {c.staff_name || "Staff"}
                          </span>
                        </td>
                        <td className="text-gray-500 dark:text-gray-400 italic">
                          {c.notes || "Complimentary"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content 4: Round Off Breakdown */}
        {state.activeTab === "round_off" && (
          <div className="bg-white dark:bg-restro-gray border border-restro-border-green rounded-[2rem] overflow-hidden shadow-sm">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-black flex items-center gap-2">
                  <IconScale size={20} className="text-blue-500" /> Round Off Adjustments Register
                </h4>
                <p className="text-xs text-gray-400">
                  Bill settlements where rounding algorithm adjusted the final payable amount
                </p>
              </div>
              <span className="badge badge-info font-bold text-xs">
                {filteredRoundOffs.length} rounded bills
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/40 text-gray-500 text-xs uppercase font-black">
                    <th>Invoice No</th>
                    <th>Date & Time</th>
                    <th>Order / Token</th>
                    <th>Table / Room</th>
                    <th className="text-right">Pre-round Total</th>
                    <th className="text-right">Round Off</th>
                    <th className="text-center">Adjustment Type</th>
                    <th className="text-right">Final Bill Amount</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoundOffs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-12 text-gray-400">
                        No round-off adjustments found for this period.
                      </td>
                    </tr>
                  ) : (
                    filteredRoundOffs.map((r, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition text-xs font-medium">
                        <td className="font-black text-gray-900 dark:text-gray-100">
                          {r.invoice_no || `#${r.invoice_id}`}
                        </td>
                        <td className="text-gray-400">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td>
                          <span className="font-bold text-gray-700 dark:text-gray-300">
                            {r.token_no ? `Token #${r.token_no}` : (r.order_id ? `Order #${r.order_id}` : "-")}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-sm badge-ghost font-bold">
                            {r.table_title || "Counter"}
                          </span>
                        </td>
                        <td className="text-right font-medium text-gray-500">
                          {currency} {Number(r.pre_round_total || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-black text-sm">
                          <span
                            className={
                              r.round_off < 0
                                ? "text-rose-600 dark:text-rose-400"
                                : "text-emerald-600 dark:text-emerald-400"
                            }
                          >
                            {r.round_off > 0 ? "+" : ""}
                            {Number(r.round_off || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="text-center">
                          {r.round_off < 0 ? (
                            <span className="badge badge-sm bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800 gap-1 font-bold">
                              <IconArrowDownRight size={14} /> Round Down (Waived)
                            </span>
                          ) : (
                            <span className="badge badge-sm bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 gap-1 font-bold">
                              <IconArrowUpRight size={14} /> Round Up
                            </span>
                          )}
                        </td>
                        <td className="text-right font-black text-gray-900 dark:text-white text-sm">
                          {currency} {Number(r.total || 0).toFixed(2)}
                        </td>
                        <td>
                          <span className="badge badge-sm badge-outline font-bold">
                            {r.payment_type || "Cash"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filter Dialog Modal */}
        <dialog id="filter-dialog" className="modal print:hidden">
          <div className="modal-box border border-restro-border-green dark:rounded-3xl p-0 overflow-hidden">
            <div className="bg-gray-900 p-8 text-white">
              <h3 className="font-black text-2xl flex items-center gap-2">
                <IconFilter stroke={2} /> Select Date Range
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Filter deductions, complimentary items and round-offs
              </p>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                  Preset Ranges
                </label>
                <select
                  className="select select-bordered w-full rounded-2xl bg-gray-50 dark:bg-gray-900 border-none font-bold"
                  ref={filterTypeRef}
                  defaultValue={state.filter}
                >
                  {filters.map((filter, index) => (
                    <option key={index} value={filter.key}>
                      {filter.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    From Date
                  </label>
                  <input
                    defaultValue={defaultDateFrom}
                    type="date"
                    ref={fromDateRef}
                    className="bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm"
                  />
                </div>
                <div className="form-control">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
                    To Date
                  </label>
                  <input
                    defaultValue={defaultDateTo}
                    type="date"
                    ref={toDateRef}
                    className="bg-gray-50 dark:bg-gray-900 border-none rounded-2xl py-3 px-4 font-bold text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => document.getElementById("filter-dialog").close()}
                  className="flex-1 py-4 font-bold text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-2xl transition active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setState((prev) => ({
                      ...prev,
                      filter: filterTypeRef.current.value,
                      fromDate: fromDateRef.current.value || null,
                      toDate: toDateRef.current.value || null,
                    }));
                    document.getElementById("filter-dialog").close();
                  }}
                  className="flex-[1.5] py-4 font-bold text-white bg-restro-green rounded-2xl hover:bg-restro-green-button-hover transition active:scale-95 shadow-lg shadow-green-100 dark:shadow-none"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </dialog>
      </ReportExportWrapper>
    </Page>
  );
}
